import * as Api from '@ViewModels';
import { css } from 'aphrodite';
import * as React from 'react';
import { Waypoint } from 'react-waypoint';
import { Topics } from '../../../../../models/LocalNotificationTopics';
import { invalidateInfiniteCampaignReportsQuery, useInfiniteCampaignReportsQuery } from '../../../../../queries';
import { filterCancelledCampaigns } from '../../../../../utils/emailFiltering';
import { LoadingSpinner } from '../../../../../web/components/LoadingSpinner';
import { LocalNotificationObserver } from '../../../../../web/components/LocalNotificationObserver';
import { Portal } from '../../../../../web/components/Portal';
import { GroupEmailTableRow } from '../../../../../web/components/reporting/emails/GroupEmailTableRow';
import { GroupEmailTableRowExpandable } from '../../../../../web/components/reporting/emails/GroupEmailTableRowExpandable';
import { groupCampaigns } from '../../../../../web/containers/reporting/Campaigns/CampaignReportingGroupedEmailsTable';
import { styleSheet } from './styles';

export function AdminReportingCampaignTable({
	accountId,
	showCancelled,
	scrollToBottomPortalId,
	onGroupItemClick,
	onCancelClicked,
	onRenderApprovalActions,
	onRescheduleClicked,
	onSubjectClicked,
}: {
	accountId: string;
	showCancelled: boolean;
	scrollToBottomPortalId: string;
	onGroupItemClick: (campaign: Api.ICampaign) => void;
	onCancelClicked: (_campaignToCancel?: Api.ICampaign) => (ev?: React.MouseEvent<HTMLElement>) => void;
	onRenderApprovalActions: (campaign: Api.ICampaign) => () => JSX.Element;
	onRescheduleClicked: (campaign: Api.ICampaign) => void;
	onSubjectClicked: (campaign: Api.ICampaign) => void;
}) {
	const impersonationContext = React.useMemo(() => ({ account: { id: accountId } }), [accountId]);
	const queryFilter = React.useMemo(
		() => ({
			op: Api.FilterOperator.And,
			criteria: [{ property: Api.BulkEmailFilterProperty.OnlyCampaigns }],
		}),
		[]
	);
	const campaignReportsQuery = useInfiniteCampaignReportsQuery({
		filter: queryFilter,
		pageSize: 100,
		impersonationContext,
	});
	const invalidateQuery = () => {
		invalidateInfiniteCampaignReportsQuery({
			filter: queryFilter,
			pageSize: 100,
			impersonationContext,
		});
	};

	const renderCampaignRows = () => {
		const allCampaigns = campaignReportsQuery.data.pages.map(page => page.values).flat();
		const filteredCampaigns = filterCancelledCampaigns(allCampaigns, showCancelled);
		const groupedCampaigns = groupCampaigns(filteredCampaigns);

		return groupedCampaigns.map(campaignOrGroup => {
			if (Array.isArray(campaignOrGroup) && campaignOrGroup.length > 1) {
				return (
					<GroupEmailTableRowExpandable
						campaigns={campaignOrGroup}
						key={`group-${campaignOrGroup[0].groupId}`}
						onCancelClicked={onCancelClicked}
						onClick={onGroupItemClick}
						onRenderApprovalActions={onRenderApprovalActions}
						onRescheduleClicked={onRescheduleClicked}
						impersonationContext={impersonationContext}
					/>
				);
			}
			const campaign = Array.isArray(campaignOrGroup) ? campaignOrGroup[0] : campaignOrGroup;
			return (
				<GroupEmailTableRow
					key={campaign.id}
					impersonationContext={impersonationContext}
					onCancelClicked={onCancelClicked(campaign)}
					onRenderApprovalActions={onRenderApprovalActions(campaign)}
					onRescheduleClicked={ev => {
						ev?.preventDefault();
						ev?.stopPropagation();
						onRescheduleClicked(campaign);
					}}
					onSubjectClicked={() => onSubjectClicked(campaign)}
					styles={[styleSheet.groupEmailTableRow]}
					campaign={campaign}
				/>
			);
		});
	};
	return (
		<>
			{campaignReportsQuery.isPending ? (
				<div className={css(styleSheet.loader)}>
					<LoadingSpinner type='large' />
				</div>
			) : null}
			{campaignReportsQuery.status === 'success' ? (
				campaignReportsQuery.data.pages[0].values.length > 0 ? (
					<>
						<header className={css(styleSheet.header)}>
							<div className={css(styleSheet.headerItem)}>Email Subject Line</div>
							<div className={css(styleSheet.headerItem)}>Sent To (Tags or Individuals)</div>
							<div className={css(styleSheet.headerItem)}>Message Analytics</div>
						</header>
						{renderCampaignRows()}
						{campaignReportsQuery.hasNextPage && !campaignReportsQuery.isFetchingNextPage ? (
							<Portal destination={scrollToBottomPortalId}>
								<Waypoint bottomOffset='-200px' onEnter={() => campaignReportsQuery.fetchNextPage()} />
							</Portal>
						) : null}
					</>
				) : (
					<div className={css(styleSheet.noneFound)}>No campaigns found</div>
				)
			) : null}
			<LocalNotificationObserver
				topic={Topics.CREATE_CAMPAIGNS_ITEM}
				onNotificationReceived={() => invalidateQuery()}
			/>
		</>
	);
}
